import { useMemo, useState } from 'react';

import { WalletCard, useListBackerConsumerWallet } from '@givebacks/api/backer';
import { Button, Container, Group, Skeleton, Stack, Title } from 'ui';

import { AccountCardList } from '../AccountCardList/AccountCardList';
import { ExpiredAccountCardsModal } from '../ExpiredAccountCardsModal/ExpiredAccountCardsModal';

type AccountCardPageProps = {
  title: string;
  filterFn: (data: WalletCard[]) => WalletCard[];
  getKey: (card: WalletCard) => string | undefined;
  noDataMessage: {
    title: string;
    message: string;
  };
};

const AccountCardPage = ({ title, filterFn, getKey, noDataMessage }: AccountCardPageProps) => {
  const { data: expiredWalletCards, isLoading } = useListBackerConsumerWallet({
    expired: true,
    select: filterFn,
  });

  const [showExpiredModal, setShowExpiredModal] = useState(false);

  const hasExpiredItems = useMemo(() => {
    return expiredWalletCards?.length && expiredWalletCards?.length > 0;
  }, [expiredWalletCards?.length]);

  // This block of code keeps the button mounted during loading and toggles its CSS display after load.
  // If we conditionally render the button, the skeleton can't measure it and we'd need a hard-coded width.
  const buttonDisplay = useMemo(() => {
    if (isLoading || hasExpiredItems) {
      return 'block';
    }

    return 'none';
  }, [hasExpiredItems, isLoading]);

  return (
    <Container size="sm" px={{ base: '0px', sm: 'xs' }}>
      <Stack gap={'md'} w="100%">
        <Group justify="space-between" align="center">
          <Title order={2}>{title}</Title>
          <Skeleton visible={isLoading} width="fit-content">
            <Button
              variant="subtle"
              size="sm"
              onClick={() => setShowExpiredModal(true)}
              display={buttonDisplay}
            >
              View Expired
            </Button>
          </Skeleton>
        </Group>
        <AccountCardList
          filterFn={filterFn}
          getKey={getKey}
          noDataTitle={noDataMessage.title}
          noDataMessage={noDataMessage.message}
        />
      </Stack>
      <ExpiredAccountCardsModal
        onClose={() => setShowExpiredModal(false)}
        opened={showExpiredModal}
        filterFn={filterFn}
        getKey={getKey}
      />
    </Container>
  );
};

export { AccountCardPage };
